import { DataTable, DataTableMessageRow } from 'components/DataTable';
import { Button } from 'components/Button';
import moment from 'moment';
import type { TDifyKnowledgeDocument } from '../../api/difyKnowledge';
import { DATE_FORMAT } from '../../constants';
import { aiKnowledgeStrings } from '../../constants/strings';

type DocumentsTableProps = {
  documents: TDifyKnowledgeDocument[];
  loading: boolean;
  selectedDocumentId: string | null;
  deletePending: boolean;
  onSelect: (documentId: string) => void;
  onDelete: (documentId: string) => void;
};

export function DocumentsTable({
  documents,
  loading,
  selectedDocumentId,
  deletePending,
  onSelect,
  onDelete,
}: DocumentsTableProps) {
  let body: JSX.Element | JSX.Element[];

  if (loading) {
    body = (
      <DataTableMessageRow colSpan={4} message={aiKnowledgeStrings.loading} />
    );
  } else if (documents.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={4}
        message={aiKnowledgeStrings.emptyDocuments}
      />
    );
  } else {
    body = documents.map((doc) => (
      <DocumentsTableRow
        key={doc.id}
        document={doc}
        isSelected={selectedDocumentId === doc.id}
        deletePending={deletePending}
        onSelect={onSelect}
        onDelete={onDelete}
      />
    ));
  }

  return (
    <DataTable
      minWidthClassName=""
      headerRow={
        <tr>
          <th className="w-[82px] px-2 py-3 text-left leading-tight whitespace-nowrap sticky left-0 z-10 bg-surface/70">
            {aiKnowledgeStrings.table.actions}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {aiKnowledgeStrings.table.name}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {aiKnowledgeStrings.table.createdAt}
          </th>
          <th className="px-2 py-3 text-left leading-tight break-words">
            {aiKnowledgeStrings.table.indexingStatus}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  );
}

type DocumentsTableRowProps = {
  document: TDifyKnowledgeDocument;
  isSelected: boolean;
  deletePending: boolean;
  onSelect: (documentId: string) => void;
  onDelete: (documentId: string) => void;
};

function DocumentsTableRow({
  document: doc,
  isSelected,
  deletePending,
  onSelect,
  onDelete,
}: DocumentsTableRowProps) {
  const createdAtLabel = doc.createdAt
    ? moment(doc.createdAt).format(`${DATE_FORMAT} HH:mm`)
    : '-';

  return (
    <tr
      className={`border-t border-border/60 bg-white transition-colors cursor-pointer hover:bg-neutral-50 ${
        isSelected ? 'bg-blue-50/80 hover:bg-blue-50/80' : ''
      }`}
      onClick={() => onSelect(doc.id)}
    >
      <td
        className={`w-[82px] px-2 py-3 text-left whitespace-nowrap sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.05)] ${
          isSelected ? 'bg-blue-50/80' : 'bg-white hover:bg-neutral-50'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <Button
          variant="outline"
          size="sm"
          disabled={deletePending}
          onClick={() => {
            if (window.confirm(aiKnowledgeStrings.deleteConfirm)) {
              onDelete(doc.id);
            }
          }}
          className="bg-red-500 text-white hover:bg-red-600 border-red-500"
        >
          {aiKnowledgeStrings.delete}
        </Button>
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
        <div className="max-w-md truncate" title={doc.name}>
          {doc.name}
        </div>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {createdAtLabel}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {doc.indexingStatus ?? '-'}
      </td>
    </tr>
  );
}
